import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SeriesCard } from "./SeriesCard";
// import ConsolidatedSummary from "./ConsolidatedSummary"; // Removed
import { codeSeries, mapEtapaToGradeName } from "@/lib/utils";
interface OrderItem {
  id: number;
  codigo_produto: string;
  descricao_produto: string;
  preco: number;
  ano_letivo: number;
  marca: string;
  sku: string;
  status: string;
  utilizacao?: string | null;
  serie?: string | null;
  segmento?: string | null;
  quantidade_contratual: number;
  desconto: number;
  quantidade_pedido: number;
  valor_liquido: number;
  valor_total: number;
  delivery_type_id: number;
  delivery_type_number: number;
  brand_id: number;
}
interface ProductTableProps {
  orderItems: OrderItem[];
  fullOrderItems: OrderItem[]; // New prop
  updateQuantidade: ( index: number, quantidade: number ) => void;
  updateQuantidadesBatch: ( updates: Array<{ id: number; quantidade: number }> ) => void; // New prop
  formatCurrency: ( value: number ) => string;
  shippingFee?: number;
  cycleYear?: number;
}
const ProductTable = ( {
  orderItems,
  fullOrderItems, // Destructure new prop
  updateQuantidade,
  updateQuantidadesBatch, // New prop
  formatCurrency,
  shippingFee,
  cycleYear = 2026,
}: ProductTableProps ) => {
  const [ openSeries, setOpenSeries ] = useState<Record<string, boolean>>( {} );

  const alunoItems = orderItems.filter(
    ( i ) => i.utilizacao?.toLowerCase() === "aluno"
  );
  const professorItems = orderItems.filter(
    ( i ) => i.utilizacao?.toLowerCase() === "professor"
  );

  const toggleSeries = ( serie: string ) => {
    setOpenSeries( ( prev ) => ( {
      ...prev,
      [ serie ]: !prev[ serie ],
    } ) );
  };
  // Map marca string to brand_id number
  const getBrandId = ( marca: string ): number => {
    const brandIdMap: Record<string, number> = {
      "1": 1,
      "2": 2,
      "3": 3,
      "4": 4,
      "5": 5,
    };
    return brandIdMap[ marca ] || 0;
  };

  const groupItemsBySeries = ( items: OrderItem[] ) => {
    const grouped: Record<string, OrderItem[]> = {};
    items.forEach( ( item ) => {
      const serie = mapEtapaToGradeName( item.serie );
      if ( !grouped[ serie ] ) {
        grouped[ serie ] = [];
      }
      grouped[ serie ].push( item );
    } );

    // Apply the same sorting logic used in useOrderCalculations
    Object.keys( grouped ).forEach( ( serie ) => {
      grouped[ serie ] = grouped[ serie ].sort( ( a, b ) => {
        // First, sort by brand_id (map from marca field)
        const brandIdA = getBrandId( a.marca );
        const brandIdB = getBrandId( b.marca );
        if ( brandIdA !== brandIdB ) {
          return brandIdA - brandIdB;
        }

        // Special handling for Plataforma AZ products to be sequential
        const aPlatform = a.descricao_produto.includes( "Plataforma AZ" );
        const bPlatform = b.descricao_produto.includes( "Plataforma AZ" );

        if ( aPlatform && bPlatform ) {
          const aFirst = a.descricao_produto.includes( "1º Semestre" );
          const bFirst = b.descricao_produto.includes( "1º Semestre" );
          if ( aFirst && !bFirst ) return -1;
          if ( !aFirst && bFirst ) return 1;
        }

        // No other sorting needed - products are sorted by brand_id only
        return 0;
      } );
    } );

    return grouped;
  };
  const handleUpdateQuantity = (
    itemId: number,
    quantity: number,
    updateAllInSeries?: boolean
  ) => {
    const originalIndex = orderItems.findIndex( ( item ) => item.id === itemId );
    if ( originalIndex !== -1 ) {
      if ( updateAllInSeries ) {
        const targetItem = orderItems[ originalIndex ];
        const targetSerie = targetItem.serie;
        const targetUtilizacao = targetItem.utilizacao;

        const updates = orderItems
          .filter(
            ( item ) =>
              item.serie === targetSerie && item.utilizacao === targetUtilizacao
          )
          .map( ( item ) => ( { id: item.id, quantidade: quantity } ) );

        updateQuantidadesBatch( updates );
      } else {
        // OrderTimeline recebe o índice dentro do segmento atual e faz o
        // mapeamento para a lista global antes de atualizar o estado.
        updateQuantidade( originalIndex, quantity );
      }
    }
  };

  const renderCardsSection = (
    items: OrderItem[],
    sectionType: "aluno" | "professor"
  ) => {
    const groupedItems = groupItemsBySeries( items );

    const sortedSeries = Object.entries( groupedItems ).sort(
      ( [ serieA ], [ serieB ] ) => {
        return codeSeries( serieA ) - codeSeries( serieB );
      }
    );

    return (
      <div className="space-y-4">
        {sortedSeries.map( ( [ serie, serieItems ] ) => {
          const uniqueKey = `${ sectionType }-${ serie }`;
          return (
            <SeriesCard
              key={uniqueKey}
              serie={serie}
              items={serieItems}
              isOpen={openSeries[ uniqueKey ] || false}
              onToggle={() => toggleSeries( uniqueKey )}
              onUpdateQuantity={handleUpdateQuantity}
              formatCurrency={formatCurrency}
              cycleYear={cycleYear}
            />
          );
        } )}
      </div>
    );
  };
  const renderSection = (
    title: string,
    items: OrderItem[],
    emptyIcon: string,
    emptyMessage: string,
    sectionType: "aluno" | "professor"
  ) => (
    <Card className="mb-8 shadow-sm border">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-semibold text-foreground flex items-center gap-2">
          <span className="text-2xl">{emptyIcon}</span>
          {title}
          {items.length > 0}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-6">
        {items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-muted-foreground/30 p-12 text-center">
            <div className="text-6xl mb-4">{emptyIcon}</div>
            <p className="text-muted-foreground text-lg">{emptyMessage}</p>
          </div>
        ) : (
          renderCardsSection( items, sectionType )
        )}
      </CardContent>
    </Card>
  );
  return (
    <div className="space-y-6">
      {renderSection(
        "Materiais para Aluno",
        alunoItems,
        "🎒",
        "Nada por aqui! Nenhum material para Aluno no momento.",
        "aluno"
      )}
      {renderSection(
        "Materiais para Professor",
        professorItems,
        "🧑‍🏫",
        "Tudo certo por aqui! Nenhum material para Professor no momento.",
        "professor"
      )}

    </div>
  );
};
export default ProductTable;
